import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adminApi } from '@/utils/adminApi';
import { CheckCircle2, Eye, EyeOff, ShieldBan, ShieldCheck, UserCog, Users, X } from 'lucide-react';

const initialForm = {
  name: '',
  email: '',
  password: '',
  ownerIds: []
};

export default function AdminDashboard() {
  const [adminProfile, setAdminProfile] = useState(null);
  const [subAdmins, setSubAdmins] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSubAdmin, setEditingSubAdmin] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState(initialForm);

  const isSuperAdmin = adminProfile?.role === 'SUPER_ADMIN';

  const assignedOwnerCount = useMemo(
    () => subAdmins.reduce((total, subAdmin) => total + (subAdmin.assignedOwnersCount || 0), 0),
    [subAdmins]
  );

  const fetchOverviewData = async () => {
    try {
      const meResponse = await adminApi.getMe();
      setAdminProfile(meResponse.data);
      localStorage.setItem('admin_profile', JSON.stringify(meResponse.data));

      if (meResponse.data.role === 'SUPER_ADMIN') {
        const [subAdminsResponse, ownersResponse] = await Promise.all([
          adminApi.getSubAdmins(),
          adminApi.getOwners()
        ]);
        setSubAdmins(subAdminsResponse.data);
        setOwners(ownersResponse.data);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to load overview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverviewData();
  }, []);

  const openCreateModal = () => {
    setEditingSubAdmin(null);
    setFormData(initialForm);
    setShowPassword(false);
    setShowCreateModal(true);
  };

  const openEditModal = (subAdmin) => {
    setEditingSubAdmin(subAdmin);
    setFormData({
      name: subAdmin.name,
      email: subAdmin.email,
      password: '',
      ownerIds: (subAdmin.assignedOwners || []).map((owner) => owner.id)
    });
    setShowCreateModal(true);
  };

  const toggleOwnerSelection = (ownerId) => {
    setFormData((current) => ({
      ...current,
      ownerIds: current.ownerIds.includes(ownerId)
        ? current.ownerIds.filter((id) => id !== ownerId)
        : [...current.ownerIds, ownerId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingSubAdmin) {
        await adminApi.updateSubAdminOwners(editingSubAdmin.id, formData.ownerIds);
        toast.success('Subadmin owner access updated');
      } else {
        await adminApi.createSubAdmin(formData);
        toast.success('Subadmin account created successfully');
      }

      setShowCreateModal(false);
      setEditingSubAdmin(null);
      setFormData(initialForm);
      fetchOverviewData();
    } catch (error) {
      toast.error(error.message || 'Failed to save subadmin');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSubAdminStatus = async (subAdminId) => {
    try {
      await adminApi.toggleSubAdminStatus(subAdminId);
      toast.success('Subadmin status updated');
      fetchOverviewData();
    } catch (error) {
      toast.error(error.message || 'Failed to update subadmin status');
    }
  };

  return (
    <AdminLayout>
      {loading ? (
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-slate-950" />
        </div>
      ) : !isSuperAdmin ? (
        <div className="flex min-h-[60vh] items-center justify-center">
          <section className="w-full max-w-3xl rounded-[34px] border border-slate-200 bg-white p-8 text-center shadow-[0_24px_70px_rgba(15,23,42,0.08)] sm:p-12">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-950 shadow-[0_18px_45px_rgba(15,23,42,0.18)]">
              <UserCog className="h-7 w-7 text-emerald-300" />
            </div>
            <h1 className="mt-6 text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">Subadmin Access</h1>
            <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-slate-600 sm:text-lg">
              Your access is limited to the owners assigned by the super admin. Use the Owners and Properties sections to manage those accounts and review their PG data.
            </p>
          </section>
        </div>
      ) : (
        <div className="space-y-6 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-2 lg:space-y-8">
          <section className="rounded-[34px] border border-slate-200 bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,0.06)] sm:p-8">
            <div className="grid gap-6 xl:grid-cols-[minmax(0,1.45fr)_auto] xl:items-end">
              <div className="min-w-0">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-emerald-700">Subadmin Control</p>
                <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
                  Create subadmins and decide which PG owners they can control.
                </h2>
                <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600 sm:text-lg">
                  A subadmin can see the assigned owner accounts, review their properties with bed counts and occupancy, and block or reactivate those owners.
                </p>
              </div>

              <Button
                onClick={openCreateModal}
                className="h-14 w-full rounded-full bg-slate-950 px-6 text-base font-semibold text-white hover:bg-slate-800 sm:w-auto xl:justify-self-end"
              >
                Create subadmin
              </Button>
            </div>
          </section>

          <section className="grid gap-5 md:grid-cols-3">
            <div className="rounded-[30px] bg-slate-950 p-6 text-white shadow-[0_20px_45px_rgba(15,23,42,0.1)]">
              <p className="text-sm font-semibold text-slate-300">Total subadmins</p>
              <p className="mt-3 text-4xl font-bold">{subAdmins.length}</p>
            </div>
            <div className="rounded-[30px] bg-emerald-500 p-6 text-white shadow-[0_20px_45px_rgba(16,185,129,0.15)]">
              <p className="text-sm font-semibold text-emerald-50">Active subadmins</p>
              <p className="mt-3 text-4xl font-bold">{subAdmins.filter((item) => item.status?.toLowerCase() === 'active').length}</p>
            </div>
            <div className="rounded-[30px] bg-cyan-500 p-6 text-white shadow-[0_20px_45px_rgba(6,182,212,0.15)]">
              <p className="text-sm font-semibold text-cyan-50">Assigned owners</p>
              <p className="mt-3 text-4xl font-bold">{assignedOwnerCount}</p>
            </div>
          </section>

          <section className="rounded-[34px] border border-slate-200 bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,0.06)]">
            <div className="flex flex-col gap-2">
              <h3 className="text-2xl font-bold text-slate-950">Subadmin directory</h3>
              <p className="text-slate-600">Grant or revoke owner access and manage subadmin account status.</p>
            </div>

            <div className="mt-6 grid gap-5 xl:grid-cols-2">
              {subAdmins.map((subAdmin) => {
                const isActive = subAdmin.status?.toLowerCase() === 'active';
                return (
                  <div key={subAdmin.id} className="rounded-[30px] border border-slate-200 bg-slate-50 p-5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <p className="break-words text-xl font-bold text-slate-950">{subAdmin.name}</p>
                        <p className="mt-1 break-words text-sm text-slate-500">{subAdmin.email}</p>
                      </div>
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                      }`}>
                        {isActive ? 'Active' : 'Blocked'}
                      </span>
                    </div>

                    <div className="mt-5 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl bg-white p-4 shadow-sm">
                        <p className="text-sm font-medium text-slate-500">Assigned owners</p>
                        <p className="mt-2 text-2xl font-bold text-slate-950">{subAdmin.assignedOwnersCount || 0}</p>
                      </div>
                      <div className="rounded-2xl bg-white p-4 shadow-sm">
                        <p className="text-sm font-medium text-slate-500">Role</p>
                        <p className="mt-2 text-2xl font-bold text-slate-950">Subadmin</p>
                      </div>
                    </div>

                    <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="text-sm font-semibold text-slate-700">Owner access</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(subAdmin.assignedOwners || []).map((owner) => (
                          <span key={owner.id} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                            {owner.name}
                          </span>
                        ))}
                        {(!subAdmin.assignedOwners || subAdmin.assignedOwners.length === 0) && (
                          <span className="text-sm text-slate-500">No owners assigned yet.</span>
                        )}
                      </div>
                    </div>

                    <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                      <Button
                        variant="outline"
                        className="flex-1 rounded-full border-slate-300"
                        onClick={() => openEditModal(subAdmin)}
                      >
                        <Users className="mr-2 h-4 w-4" />
                        Manage owner access
                      </Button>
                      <Button
                        variant="outline"
                        className={`flex-1 rounded-full ${
                          isActive
                            ? 'border-rose-200 text-rose-700 hover:bg-rose-50'
                            : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                        }`}
                        onClick={() => handleToggleSubAdminStatus(subAdmin.id)}
                      >
                        {isActive ? (
                          <>
                            <ShieldBan className="mr-2 h-4 w-4" />
                            Block subadmin
                          </>
                        ) : (
                          <>
                            <ShieldCheck className="mr-2 h-4 w-4" />
                            Activate subadmin
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}

              {subAdmins.length === 0 && (
                <div className="rounded-[30px] border border-dashed border-slate-300 bg-slate-50 px-4 py-14 text-center text-slate-500 xl:col-span-2">
                  No subadmins created yet.
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-3xl rounded-[34px] border border-slate-200 bg-white p-6 shadow-[0_30px_90px_rgba(15,23,42,0.22)] sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-700">
                  {editingSubAdmin ? 'Update Access' : 'Create Subadmin'}
                </p>
                <h3 className="mt-2 text-2xl font-bold text-slate-950">
                  {editingSubAdmin ? 'Update subadmin owner access' : 'Create a subadmin account'}
                </h3>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  {editingSubAdmin
                    ? 'Choose which PG owners this subadmin can manage.'
                    : 'Set login credentials and assign PG owners this subadmin can access.'}
                </p>
              </div>

              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingSubAdmin(null);
                  setFormData(initialForm);
                }}
                className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              {!editingSubAdmin && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="mb-2 block text-sm font-semibold text-slate-700">Full name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter full name"
                      className="h-12 rounded-2xl border-slate-200"
                      required
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-sm font-semibold text-slate-700">Email address</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="subadmin@example.com"
                      className="h-12 rounded-2xl border-slate-200"
                      required
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="mb-2 block text-sm font-semibold text-slate-700">Password</Label>
                    <div className="group flex h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition-all focus-within:border-emerald-300 focus-within:ring-4 focus-within:ring-emerald-100">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Set a login password"
                        className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((value) => !value)}
                        className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition group-focus-within:bg-emerald-50 group-focus-within:text-emerald-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label className="mb-3 block text-sm font-semibold text-slate-700">Assign PG owners</Label>
                <div className="grid max-h-[320px] gap-3 overflow-y-auto rounded-[28px] border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
                  {owners.map((owner) => {
                    const isSelected = formData.ownerIds.includes(owner.id);
                    return (
                      <button
                        type="button"
                        key={owner.id}
                        onClick={() => toggleOwnerSelection(owner.id)}
                        className={`rounded-2xl border px-4 py-4 text-left transition-all ${
                          isSelected
                            ? 'border-slate-950 bg-slate-950 text-white'
                            : 'border-slate-200 bg-white text-slate-900 hover:border-slate-300'
                        }`}
                      >
                        <p className="font-semibold">{owner.name}</p>
                        <p className={`mt-1 text-sm ${isSelected ? 'text-slate-300' : 'text-slate-500'}`}>{owner.email}</p>
                        <p className={`mt-2 text-xs font-semibold ${isSelected ? 'text-emerald-300' : 'text-emerald-700'}`}>
                          {owner.totalPgs} PGs • {owner.totalBeds} beds
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                <div className="flex items-center gap-2 font-semibold">
                  <CheckCircle2 className="h-4 w-4" />
                  Assigned subadmins can see those owners, their properties, bed size, occupancy, and can block or activate those owner accounts.
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 pt-1 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 rounded-full border-slate-300"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingSubAdmin(null);
                    setFormData(initialForm);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="h-12 rounded-full bg-slate-950 px-6 text-white hover:bg-slate-800"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : editingSubAdmin ? 'Update access' : 'Create subadmin'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
